import { HTMLTableProps, ResizeEntry, ResizeSensor } from "@blueprintjs/core";
import produce from "immer";
import { uniqBy } from "lodash";
import sortBy from "lodash/sortBy";
import * as React from "react";
import styled, { FlattenSimpleInterpolation } from "styled-components";
import { SelectKeyColumn } from "./components/SelectKeyColumn";
import { IGroupRowInfo } from "./components/IGroupRowInfo";
import {
  ColumnCustomizationInfo,
  IRowFunctions,
  IUseGridStateReturn,
} from "nsitools-react";
import { LoadingOverlay } from "./components/LoadingOverlay";
import { FilterInput } from "./components/FilterInput";
import { ColumnSelector } from "./components/ColumnSelector";
import { DataGrid } from "./components/DataGrid";
import { PageSelector } from "./components/PageSelector";
import { IDataTableColumn } from "./components/IDataTableColumn";

export interface IDataTable2Props<T> {
  tableState: IUseGridStateReturn<T>;
  loading?: boolean;
  formatDate?: (date: Date) => string;
  columns?: Array<IDataTableColumn<any>>;
  groupInfos?: Array<IGroupRowInfo>;
  customizeTableStyle?: () => FlattenSimpleInterpolation;
  footerStyle?: React.CSSProperties;
  customizeRowStyle?: (item: T) => FlattenSimpleInterpolation;
  renderSubComponent?: (item: T) => React.ReactElement | string;
  onRowClick?: (item: T, rowFunctions?: IRowFunctions) => void;
  htmlTableOptions?: HTMLTableProps;
  renderNoData?: React.ReactElement;
  interactive?: boolean;
  keyFieldName?: string;
  showHeader?: boolean;
  showPageSizeSelector?: boolean;
  filterMode?: "OnChange" | "OnBlurOrEnter" | "OnEnter";
  focusOnFilter?: boolean;
  filterPlaceholder?: string;
  largeFilter?: boolean;
  className?: string;
  onInputEnter?: (value: string) => void;
  translateFunc?: (code: string) => string;
  stickyHeader?: boolean;
  showColumnSelector?: boolean;
  customLoadingComponent?: React.ReactNode;
  highlightFilter?: boolean;
  filterDebounceInMs?: number;
  onToggleSelectAll?: () => Promise<Set<any>> | Set<any>;
  tableLayoutFixed?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

interface IDatatable2Context<T> extends IDataTable2Props<T> {
  renderColumnHeader: (col: IDataTableColumn<any>) => React.ReactNode;
  tableState: IUseGridStateReturn<T>;
  translateFunc: (s: string) => string;
  visibleColumns: Array<IDataTableColumn<T>>;
  setColumnOrder: (cols: IDataTableColumn<T>[]) => void;
  resetColumnsState: () => void;
  toggleColumnVisibility: (col: IDataTableColumn<T>, visible: boolean) => void;
  isColumnVisible: (col: IDataTableColumn<T>) => boolean;
}

const TopGridContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  & > * + * {
    margin-left: 0.5rem;
  }
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const Datatable2Context = React.createContext<IDatatable2Context<any>>(null);

export function DataTable2<T>(props: IDataTable2Props<T>) {
  const {
    columns,
    showColumnSelector,
    tableState,
    className,
    onToggleSelectAll,
    tableLayoutFixed = false,
  } = props;

  const selectColumn = SelectKeyColumn({ onToggleSelectAll, tableState });

  const columnsWithSelection = React.useMemo(() => {
    if (tableState?.enableSelection) {
      return [selectColumn, ...columns];
    } else {
      return columns;
    }
  }, [columns, selectColumn, tableState?.enableSelection]);

  const [componentSize, setComponentSize] = React.useState<ResizeEntry>();
  const handleResize = React.useCallback((entries: ResizeEntry[]) => {
    setComponentSize(entries.reverse()[0]);
  }, []);

  const renderColumnHeader = React.useCallback((col: IDataTableColumn<any>) => {
    if (col.header) return col.header();
    if (col.computed) return <></>;
    return <>{col.fieldName}</>;
  }, []);

  const initialColumnCustomizations = React.useMemo(
    () =>
      columnsWithSelection.map((c, i) => ({
        fieldName: c.fieldName,
        position: i,
        visible: !c.hidden,
      })),
    [columnsWithSelection]
  );
  const resetColumnsState = React.useCallback(() => {
    tableState.setColumnCustomizations(initialColumnCustomizations);
  }, [initialColumnCustomizations, tableState]);

  React.useEffect(() => {
    // synchronize customization with columns
    if (!tableState.columnCustomizations) {
      resetColumnsState();
    } else {
      const missingColumns = columnsWithSelection
        .filter((d) => d.disableCustomization !== true)
        .filter(
          (d) =>
            !tableState.columnCustomizations.find(
              (c) => c.fieldName === d.fieldName
            )
        );

      const droppedColumns = tableState.columnCustomizations.filter(
        (cust) =>
          !columnsWithSelection.some((c) => c.fieldName === cust.fieldName)
      );

      if (missingColumns.length === 0 && droppedColumns.length === 0) return;

      let maxPosition = Math.max(
        ...tableState.columnCustomizations.map((d) => d.position)
      );
      const additionalColumns = missingColumns.map(
        (c) =>
          ({
            fieldName: c.fieldName,
            position: ++maxPosition,
            visible: !c.hidden,
          } as ColumnCustomizationInfo)
      );
      tableState.setColumnCustomizations((prev) =>
        uniqBy(
          [
            ...prev.filter(
              (c) => !droppedColumns.some((d) => d.fieldName === c.fieldName)
            ),
            ...additionalColumns,
          ],
          "fieldName"
        )
      );
    }
  }, [
    columnsWithSelection,
    resetColumnsState,
    tableState,
    tableState.columnCustomizations,
  ]);

  const visibleColumns = React.useMemo(() => {
    const customizationInfos =
      tableState.columnCustomizations ?? initialColumnCustomizations;
    const sortedColumnCustomizations = sortBy(
      customizationInfos,
      (c) => c.position
    );
    const validColumns = sortedColumnCustomizations.filter(
      (cust) =>
        cust.visible &&
        columnsWithSelection.map((d) => d.fieldName).includes(cust.fieldName)
    );

    return validColumns.map((c) =>
      columnsWithSelection.find((d) => d.fieldName === c.fieldName)
    );
  }, [
    columnsWithSelection,
    initialColumnCustomizations,
    tableState.columnCustomizations,
  ]);

  const setColumnOrder = React.useCallback(
    (cols: IDataTableColumn<T>[]) => {
      const tempColumnCustomizationInfos = [...tableState.columnCustomizations];
      for (let i = 0; i < cols.length; i++) {
        const col = cols[i];
        const colInfosIndex = tempColumnCustomizationInfos?.findIndex(
          (c) => c.fieldName === col?.fieldName
        );
        if (colInfosIndex > -1) {
          tempColumnCustomizationInfos[colInfosIndex] = {
            ...tempColumnCustomizationInfos[colInfosIndex],
            position: i,
          };
        }
      }
      tableState.setColumnCustomizations([...tempColumnCustomizationInfos]);
    },
    [tableState]
  );

  const isColumnVisible = React.useCallback(
    (col: IDataTableColumn<T>) => {
      const found = tableState.columnCustomizations.find(
        (d) => d.fieldName === col.fieldName
      );
      return found.visible;
    },
    [tableState.columnCustomizations]
  );

  const toggleColumnVisibility = React.useCallback(
    (col: IDataTableColumn<T>, visible: boolean) =>
      tableState.setColumnCustomizations((prev) =>
        produce(prev, (draft) => {
          const found = draft.find((d) => d.fieldName === col.fieldName);
          found.visible = visible;
        })
      ),
    [tableState]
  );

  return (
    <Datatable2Context.Provider
      value={{
        ...props,
        columns: columnsWithSelection,
        translateFunc: props.translateFunc ? props.translateFunc : (s) => s,
        renderColumnHeader,
        tableState,
        visibleColumns,
        setColumnOrder,
        resetColumnsState,
        toggleColumnVisibility,
        isColumnVisible,
      }}
    >
      <ResizeSensor onResize={handleResize}>
        <Container className={className}>
          <LoadingOverlay
            overlayHeight={componentSize?.contentRect.height}
            overlayWidth={componentSize?.contentRect.width}
          ></LoadingOverlay>
          <TopGridContainer>
            {tableState.enableFilter && <FilterInput></FilterInput>}
            {showColumnSelector && <ColumnSelector></ColumnSelector>}
          </TopGridContainer>
          <GridContainer>
            <DataGrid fixed={true} side="left"></DataGrid>
            <DataGrid fixedLayout={tableLayoutFixed}></DataGrid>
            <DataGrid fixed={true} side="right"></DataGrid>
          </GridContainer>
          {tableState.enablePagination && <PageSelector></PageSelector>}
        </Container>
      </ResizeSensor>
    </Datatable2Context.Provider>
  );
}

export const useDatatable2Context = () => React.useContext(Datatable2Context);
